/**
 * Performance monitoring and optimization utilities
 * Tracks Core Web Vitals and provides performance insights
 */

import { analytics } from '../services/analytics'

// Health check and monitoring utilities
interface HealthCheckResult {
  status: 'healthy' | 'degraded' | 'unhealthy'
  timestamp: number
  checks: {
    [key: string]: {
      status: 'pass' | 'warn' | 'fail'
      value?: any
      message?: string
    }
  }
}

class HealthMonitor {
  private checkInterval: number
  private intervalId: NodeJS.Timeout | null = null
  private lastHealthCheck: HealthCheckResult | null = null
  
  constructor(checkIntervalMs: number = 30000) { // 30 seconds default
    this.checkInterval = checkIntervalMs
  }
  
  start() {
    if (this.intervalId) return
    
    this.intervalId = setInterval(() => {
      this.performHealthCheck()
    }, this.checkInterval)
    
    // Perform initial check
    this.performHealthCheck()
  }
  
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
  }
  
  async performHealthCheck(): Promise<HealthCheckResult> {
    const checks: HealthCheckResult['checks'] = {}
    
    // Memory check
    if ('memory' in performance) {
      const memory = (performance as any).memory
      const memoryUsagePercent = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100
      
      checks.memory = {
        status: memoryUsagePercent > 80 ? 'fail' : memoryUsagePercent > 60 ? 'warn' : 'pass',
        value: `${Math.round(memoryUsagePercent)}%`,
        message: memoryUsagePercent > 80 ? 'High memory usage' : undefined
      }
    }
    
    // Network connectivity check
    checks.network = {
      status: navigator.onLine ? 'pass' : 'fail',
      value: navigator.onLine,
      message: navigator.onLine ? undefined : 'No network connection'
    }
    
    // Connection quality check
    if ('connection' in navigator) {
      const connection = (navigator as any).connection
      const isSlowConnection = connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g'
      
      checks.connectionQuality = {
        status: isSlowConnection ? 'warn' : 'pass',
        value: connection.effectiveType,
        message: isSlowConnection ? 'Slow network detected' : undefined
      }
    }
    
    // Local storage availability
    try {
      localStorage.setItem('health-check', 'test')
      localStorage.removeItem('health-check')
      checks.localStorage = { status: 'pass', value: true }
    } catch (error) {
      checks.localStorage = { 
        status: 'warn', 
        value: false, 
        message: 'Local storage not available' 
      }
    }
    
    // Service worker status
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration()
      checks.serviceWorker = {
        status: registration ? 'pass' : 'warn',
        value: !!registration,
        message: registration ? undefined : 'Service worker not registered'
      }
    }
    
    // Audio support check
    const audio = document.createElement('audio')
    const hlsSupported = audio.canPlayType('application/vnd.apple.mpegurl') || 
                        (window as any).Hls?.isSupported()
    
    checks.audioSupport = {
      status: hlsSupported ? 'pass' : 'fail',
      value: hlsSupported,
      message: hlsSupported ? undefined : 'HLS audio not supported'
    }
    
    // Overall status determination
    const failedChecks = Object.values(checks).filter(check => check.status === 'fail')
    const warnChecks = Object.values(checks).filter(check => check.status === 'warn')
    
    let status: HealthCheckResult['status']
    if (failedChecks.length > 0) {
      status = 'unhealthy'
    } else if (warnChecks.length > 0) {
      status = 'degraded'
    } else {
      status = 'healthy'
    }
    
    const result: HealthCheckResult = {
      status,
      timestamp: Date.now(),
      checks
    }
    
    this.lastHealthCheck = result
    
    // Log health status changes
    if (status !== 'healthy') {
      console.warn('Health check warning/failure:', result)
      analytics.trackEvent('error_occurred', {
        errorType: 'session',
        errorMessage: `Health check ${status}: ${failedChecks.length} failures, ${warnChecks.length} warnings`
      })
    }
    
    return result
  }
  
  getLastHealthCheck(): HealthCheckResult | null {
    return this.lastHealthCheck
  }
}

export const healthMonitor = new HealthMonitor()

// Core Web Vitals thresholds (Google standards)
const PERFORMANCE_THRESHOLDS = {
  // First Contentful Paint
  FCP_GOOD: 1800, // 1.8s
  FCP_POOR: 3000, // 3s
  
  // Largest Contentful Paint  
  LCP_GOOD: 2500, // 2.5s
  LCP_POOR: 4000, // 4s
  
  // First Input Delay
  FID_GOOD: 100,  // 100ms
  FID_POOR: 300,  // 300ms
  
  // Cumulative Layout Shift
  CLS_GOOD: 0.1,
  CLS_POOR: 0.25,
  
  // Time to First Play (custom metric)
  TTFP_TARGET: 2500 // 2.5s target for audio loading
}

interface PerformanceMetric {
  name: string
  value: number
  rating: 'good' | 'needs-improvement' | 'poor'
  timestamp: number
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = []
  private observer: PerformanceObserver | null = null
  
  constructor() {
    this.initializeObserver()
    this.trackNavigationTiming()
  }
  
  private initializeObserver() {
    if ('PerformanceObserver' in window) {
      this.observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          this.processPerformanceEntry(entry)
        }
      })
      
      // Observe layout shifts, paint events, and navigation
      try {
        this.observer.observe({ entryTypes: ['layout-shift', 'paint', 'navigation', 'first-input'] })
      } catch (error) {
        console.warn('Performance Observer not fully supported:', error)
      }
    }
  }
  
  private processPerformanceEntry(entry: PerformanceEntry) {
    const timestamp = Date.now()
    
    switch (entry.entryType) {
      case 'paint':
        if (entry.name === 'first-contentful-paint') {
          this.recordMetric('FCP', entry.startTime, this.rateFCP(entry.startTime), timestamp)
        }
        break
        
      case 'largest-contentful-paint':
        const lcpEntry = entry as any
        this.recordMetric('LCP', lcpEntry.startTime, this.rateLCP(lcpEntry.startTime), timestamp)
        break
        
      case 'first-input':
        const fidEntry = entry as any
        const fid = fidEntry.processingStart - fidEntry.startTime
        this.recordMetric('FID', fid, this.rateFID(fid), timestamp)
        break
        
      case 'layout-shift':
        const clsEntry = entry as any
        if (!clsEntry.hadRecentInput) {
          this.recordMetric('CLS', clsEntry.value, this.rateCLS(clsEntry.value), timestamp)
        }
        break
    }
  }
  
  private recordMetric(name: string, value: number, rating: 'good' | 'needs-improvement' | 'poor', timestamp: number) {
    const metric: PerformanceMetric = { name, value, rating, timestamp }
    this.metrics.push(metric)
    
    // Send to analytics
    analytics.trackPerformanceMetric(name, value, rating)
    
    console.log(`Performance metric - ${name}: ${value.toFixed(2)}ms (${rating})`)
  }
  
  private rateFCP(value: number): 'good' | 'needs-improvement' | 'poor' {
    if (value <= PERFORMANCE_THRESHOLDS.FCP_GOOD) return 'good'
    if (value <= PERFORMANCE_THRESHOLDS.FCP_POOR) return 'needs-improvement'
    return 'poor'
  }
  
  private rateLCP(value: number): 'good' | 'needs-improvement' | 'poor' {
    if (value <= PERFORMANCE_THRESHOLDS.LCP_GOOD) return 'good'
    if (value <= PERFORMANCE_THRESHOLDS.LCP_POOR) return 'needs-improvement'
    return 'poor'
  }
  
  private rateFID(value: number): 'good' | 'needs-improvement' | 'poor' {
    if (value <= PERFORMANCE_THRESHOLDS.FID_GOOD) return 'good'
    if (value <= PERFORMANCE_THRESHOLDS.FID_POOR) return 'needs-improvement'
    return 'poor'
  }
  
  private rateCLS(value: number): 'good' | 'needs-improvement' | 'poor' {
    if (value <= PERFORMANCE_THRESHOLDS.CLS_GOOD) return 'good'
    if (value <= PERFORMANCE_THRESHOLDS.CLS_POOR) return 'needs-improvement'
    return 'poor'
  }
  
  private trackNavigationTiming() {
    // Track page load performance
    window.addEventListener('load', () => {
      setTimeout(() => {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
        
        if (navigation) {
          const pageLoad = navigation.loadEventEnd - (navigation as any).navigationStart
          const domContentLoaded = navigation.domContentLoadedEventEnd - (navigation as any).navigationStart
          
          this.recordMetric('PageLoad', pageLoad, pageLoad <= 3000 ? 'good' : pageLoad <= 5000 ? 'needs-improvement' : 'poor', Date.now())
          this.recordMetric('DOMContentLoaded', domContentLoaded, domContentLoaded <= 2000 ? 'good' : domContentLoaded <= 3000 ? 'needs-improvement' : 'poor', Date.now())
        }
      }, 0)
    })
  }
  
  // Custom TTFP tracking for audio loading
  public trackTTFP(startTime: number, audioReadyTime: number) {
    const ttfp = audioReadyTime - startTime
    const rating = ttfp <= PERFORMANCE_THRESHOLDS.TTFP_TARGET ? 'good' : 
                   ttfp <= PERFORMANCE_THRESHOLDS.TTFP_TARGET * 1.5 ? 'needs-improvement' : 'poor'
    
    this.recordMetric('TTFP', ttfp, rating, Date.now())
    
    // Track in analytics with additional context
    analytics.trackEvent('audio_play_start', {
      ttfp: ttfp,
      playbackDuration: 0
    })
  }
  
  // Get performance summary
  public getPerformanceSummary(): { [key: string]: PerformanceMetric } {
    const summary: { [key: string]: PerformanceMetric } = {}
    
    // Get the latest metric for each type
    for (const metric of this.metrics) {
      if (!summary[metric.name] || metric.timestamp > summary[metric.name].timestamp) {
        summary[metric.name] = metric
      }
    }
    
    return summary
  }
  
  // Memory usage tracking
  public trackMemoryUsage() {
    if ('memory' in performance) {
      const memory = (performance as any).memory
      const memoryUsage = {
        used: Math.round(memory.usedJSHeapSize / 1048576), // MB
        total: Math.round(memory.totalJSHeapSize / 1048576), // MB
        limit: Math.round(memory.jsHeapSizeLimit / 1048576)  // MB
      }
      
      console.log('Memory usage:', memoryUsage)
      
      // Alert if memory usage is high
      const usagePercent = (memoryUsage.used / memoryUsage.limit) * 100
      if (usagePercent > 80) {
        analytics.trackError('general', `High memory usage: ${usagePercent.toFixed(1)}%`)
      }
      
      return memoryUsage
    }
    
    return null
  }
  
  // Network information tracking
  public trackNetworkInfo() {
    if ('connection' in navigator) {
      const connection = (navigator as any).connection
      const networkInfo = {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt
      }
      
      console.log('Network info:', networkInfo)
      
      // Adjust quality based on network conditions
      if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') {
        analytics.trackEvent('error_occurred', {
          errorType: 'network',
          errorMessage: `Slow network detected: ${connection.effectiveType}`
        })
      }
      
      return networkInfo
    }
    
    return null
  }
  
  public destroy() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }
}

// Export singleton instance
export const performanceMonitor = new PerformanceMonitor()

// Export utility functions
export const preloadAudioSegments = async (manifestUrl: string): Promise<void> => {
  try {
    const response = await fetch(manifestUrl)
    const manifest = await response.text()
    
    // Parse M3U8 and preload first few segments
    const lines = manifest.split('\n')
    const segments: string[] = []
    
    for (const line of lines) {
      if (line.endsWith('.ts')) {
        segments.push(line)
        if (segments.length >= 3) break // Preload first 3 segments
      }
    }
    
    // Preload segments in parallel
    const preloadPromises = segments.map(segment => {
      try {
        const segmentUrl = segment.startsWith('http') ? segment : new URL(segment.trim(), manifestUrl).href
        return fetch(segmentUrl).then(response => {
          if (response.ok) {
            console.log('Preloaded audio segment:', segment)
          }
        }).catch(error => {
          console.warn('Failed to preload segment:', segment, error)
        })
      } catch (error) {
        console.warn('Invalid segment URL:', segment, error)
        return Promise.resolve()
      }
    })
    
    await Promise.allSettled(preloadPromises)
    
  } catch (error) {
    console.warn('Failed to preload audio segments:', error)
  }
}

// Image optimization utilities
export const optimizeImageLoading = () => {
  // Add intersection observer for lazy loading
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement
          if (img.dataset.src) {
            img.src = img.dataset.src
            img.removeAttribute('data-src')
            imageObserver.unobserve(img)
          }
        }
      })
    })
    
    // Observe all images with data-src
    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img)
    })
  }
}